"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  NumberResult,
  type ResultLine,
} from "@/components/tools/numbers-languages/number-result";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type ChineseForm,
  type ChineseLargeUnits,
  type ChineseNote,
  type ChineseReadError,
  type ChineseReadNote,
  type ChineseScript,
  type ChineseStyle,
  chineseRead,
  chineseSpell,
  splitAmount,
} from "@/lib/tool-kit/numbers-languages/chinese";
import {
  type DecimalNumber,
  type ReadError,
  readDecimalNumber,
} from "@/lib/tool-kit/numbers-languages/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const CHINESE_NUMBER_CONVERTER_STRINGS = {
  directionLabel: "Convert",
  toChinese: "Number → Chinese",
  fromChinese: "Chinese → number",
  numberLabel: "Number",
  numberHint:
    "Whole numbers up to 24 digits; a decimal point or comma, a minus sign and spaces between digit groups are fine.",
  numberPlaceholder: "123456789",
  chineseLabel: "Chinese numerals",
  chineseHint:
    "Standard (一百二十), financial (壹佰贰拾), traditional (一萬), digit by digit (二〇二六) and RMB amounts (壹佰元整) are all read.",
  chinesePlaceholder: "一亿二千三百四十五万六千七百八十九",
  styleLabel: "Numerals",
  styleStandard: "Standard (一二三)",
  styleFinancial: "Financial 大写 (壹贰叁)",
  scriptLabel: "Script",
  scriptSimplified: "Simplified (万 亿)",
  scriptTraditional: "Traditional (萬 億)",
  formLabel: "Form",
  formFull: "Full numerals (一千二百)",
  formDigits: "Digit by digit (一二〇〇)",
  formAmount: "RMB amount (元 角 分)",
  unitsLabel: "Units above 亿",
  unitsCompound: "万亿, 亿亿 (mainland usage)",
  unitsLadder: "兆, 京, 垓 (classical ladder)",
  liang: "Use 两 for a spoken two (两百, 两千, 两万)",
  circleZero: "Write zero as 〇",
  resultHeading: "In Chinese",
  readHeading: "As a number",
  copy: "Copy",
  copied: "Copied!",
  mixedLabel: "Mixed form",
  groupedThreesLabel: "In threes",
  groupedFoursLabel: "In fours (万 / 亿)",
  rulesHeading: "Why it is written this way",
  enterPrompt: "Type a number to see it in Chinese.",
  enterChinesePrompt: "Type Chinese numerals to read them as a number.",
  errorInvalid:
    "That is not a number. Use digits, with an optional minus sign and decimal point.",
  errorTooBig: "Numbers are limited to 24 digits (below 10²⁴).",
  errorTooLong: "Keep the decimal part to 20 digits or fewer.",
  errorAmount: "An RMB amount has at most two decimal places (角 and 分).",
  errorReadInvalid:
    "These characters do not form a Chinese number. Check the digits and units.",
  errorReadOrder:
    "The units are out of order or repeated. Units must fall in size: 千 百 十, and 万 before 亿.",
  errorReadTooBig: "That number has more than 24 digits.",
  noteFourDigitGroups:
    "Chinese groups digits in fours, not threes: 万 opens at 10⁴ and 亿 at 10⁸.",
  noteZero:
    "One 零 stands for any run of zeros between non-zero digits, even across a group boundary or a skipped group. Trailing zeros are not written.",
  noteTenOmitsOne:
    "A leading 十 is written without 一 (十五), but 一 returns once the number has started (一百一十五).",
  noteFinancial:
    "Financial numerals (大写) use 壹贰叁肆伍陆柒捌玖拾佰仟 so that no stroke can be added to an amount. 壹拾 keeps its 壹.",
  noteLiang:
    "两 replaces 二 before 百, 千, 万 and 亿 in speech. 二 is still used in tens and after 万, as in 二十 and 一万二千 or 一万两千.",
  noteLargeUnits:
    "Above 亿 mainland usage stacks the units (万亿 is 10¹², 亿亿 is 10¹⁶); 兆, 京 and 垓 are the classical names.",
  noteAmount:
    "RMB amounts start with 人民币, end in 元整 when there are no 角 or 分, write 零 between 元 and a lone 分, and take no 整 after 分.",
  noteColloquial:
    "A bare digit after a unit is read as the next unit down, as spoken: 一百二 is 120, 三万五 is 35 000. Add 零 for exact values: 三万零五 is 30 005.",
  noteDigitByDigit:
    "Digits with no units are read one by one, as years and phone numbers are written: 二〇二六 is 2026.",
  noteReadFinancial: "Financial numerals (大写) are read like ordinary ones.",
  noteReadAmount:
    "Read as an RMB amount: the decimals are 角 (tenths) and 分 (hundredths).",
} as const;

type Strings = typeof CHINESE_NUMBER_CONVERTER_STRINGS;
type Direction = "toChinese" | "fromChinese";

const NOTE_KEYS: Record<ChineseNote, keyof Strings> = {
  fourDigitGroups: "noteFourDigitGroups",
  zero: "noteZero",
  tenOmitsOne: "noteTenOmitsOne",
  financial: "noteFinancial",
  liang: "noteLiang",
  amount: "noteAmount",
  largeUnits: "noteLargeUnits",
};

const READ_NOTE_KEYS: Record<ChineseReadNote, keyof Strings> = {
  colloquial: "noteColloquial",
  digitByDigit: "noteDigitByDigit",
  financial: "noteReadFinancial",
  amount: "noteReadAmount",
};

const READ_ERROR_KEYS: Record<ChineseReadError, keyof Strings | null> = {
  empty: null,
  invalid: "errorReadInvalid",
  order: "errorReadOrder",
  tooBig: "errorReadTooBig",
};

interface Controls {
  style: ChineseStyle;
  script: ChineseScript;
  form: ChineseForm;
  units: ChineseLargeUnits;
  liang: boolean;
  circleZero: boolean;
}

interface View {
  words: string;
  lang: string;
  lines: ResultLine[];
  notes: string[];
}

type Outcome = { view: View } | { error: string } | null;

function readErrorText(error: ReadError, strings: Strings): string | null {
  switch (error) {
    case "empty":
      return null;
    case "tooBig":
      return strings.errorTooBig;
    case "tooLong":
      return strings.errorTooLong;
    default:
      return strings.errorInvalid;
  }
}

function spellOutcome(
  value: DecimalNumber,
  controls: Controls,
  strings: Strings
): Outcome {
  const options = { ...controls };
  if (
    controls.form === "amount" &&
    splitAmount(value.integer, value.fraction) === null
  ) {
    return { error: strings.errorAmount };
  }
  const result = chineseSpell(
    value.negative,
    value.integer,
    value.fraction,
    options
  );
  const lines: ResultLine[] = [];
  if (result.mixed !== null) {
    lines.push({ label: strings.mixedLabel, text: result.mixed, lang: "zh" });
  }
  return {
    view: {
      words: result.words,
      lang: controls.script === "simplified" ? "zh-Hans" : "zh-Hant",
      lines,
      notes: result.notes.map((note) => strings[NOTE_KEYS[note]]),
    },
  };
}

/** Digits grouped from the right in runs of `size`, separated by a space. */
function groupDigits(digits: string, size: number): string {
  const parts: string[] = [];
  for (let end = digits.length; end > 0; end -= size) {
    parts.unshift(digits.slice(Math.max(0, end - size), end));
  }
  return parts.join(" ");
}

function readOutcome(raw: string, strings: Strings): Outcome {
  const result = chineseRead(raw);
  if (!result.ok) {
    const key = READ_ERROR_KEYS[result.error];
    return key === null ? null : { error: strings[key] };
  }
  const decimal = result.fraction === "" ? "" : `.${result.fraction}`;
  const sign = result.negative ? "-" : "";
  const lines: ResultLine[] = [];
  if (result.integer.length > 3) {
    lines.push({
      label: strings.groupedThreesLabel,
      text: `${sign}${groupDigits(result.integer, 3)}${decimal}`,
    });
    lines.push({
      label: strings.groupedFoursLabel,
      text: `${sign}${groupDigits(result.integer, 4)}${decimal}`,
    });
  }
  return {
    view: {
      words: `${sign}${result.integer}${decimal}`,
      lang: "en",
      lines,
      notes: result.notes.map((note) => strings[READ_NOTE_KEYS[note]]),
    },
  };
}

function evaluate(
  direction: Direction,
  raw: string,
  chinese: string,
  controls: Controls,
  strings: Strings
): Outcome {
  if (direction === "fromChinese") {
    return readOutcome(chinese, strings);
  }
  const read = readDecimalNumber(raw);
  if (!read.ok) {
    const message = readErrorText(read.error, strings);
    return message === null ? null : { error: message };
  }
  return spellOutcome(read.value, controls, strings);
}

export default function ChineseNumberConverterWidget() {
  const strings = useToolUi(CHINESE_NUMBER_CONVERTER_STRINGS);
  const [direction, setDirection] = useState<Direction>("toChinese");
  const [raw, setRaw] = useState("123456789");
  const [chinese, setChinese] = useState("");
  const [controls, setControls] = useState<Controls>({
    style: "standard",
    script: "simplified",
    form: "full",
    units: "compound",
    liang: false,
    circleZero: false,
  });
  const set = <K extends keyof Controls>(key: K, value: Controls[K]) =>
    setControls((current) => ({ ...current, [key]: value }));
  const outcome = evaluate(direction, raw, chinese, controls, strings);
  const forward = direction === "toChinese";

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.directionLabel}
        name="zh-direction"
        onChange={setDirection}
        options={[
          { value: "toChinese", label: strings.toChinese },
          { value: "fromChinese", label: strings.fromChinese },
        ]}
        value={direction}
      />
      {forward ? (
        <TextField
          hint={strings.numberHint}
          id="zh-number"
          label={strings.numberLabel}
          numeric
          onChange={setRaw}
          placeholder={strings.numberPlaceholder}
          value={raw}
        />
      ) : (
        <TextField
          hint={strings.chineseHint}
          id="zh-chinese"
          label={strings.chineseLabel}
          onChange={setChinese}
          placeholder={strings.chinesePlaceholder}
          value={chinese}
        />
      )}
      {forward ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="zh-style"
            label={strings.styleLabel}
            onChange={(value) => set("style", value)}
            options={[
              { value: "standard", label: strings.styleStandard },
              { value: "financial", label: strings.styleFinancial },
            ]}
            value={controls.style}
          />
          <SelectField
            id="zh-script"
            label={strings.scriptLabel}
            onChange={(value) => set("script", value)}
            options={[
              { value: "simplified", label: strings.scriptSimplified },
              { value: "traditional", label: strings.scriptTraditional },
            ]}
            value={controls.script}
          />
          <SelectField
            id="zh-form"
            label={strings.formLabel}
            onChange={(value) => set("form", value)}
            options={[
              { value: "full", label: strings.formFull },
              { value: "digits", label: strings.formDigits },
              { value: "amount", label: strings.formAmount },
            ]}
            value={controls.form}
          />
          <SelectField
            id="zh-units"
            label={strings.unitsLabel}
            onChange={(value) => set("units", value)}
            options={[
              { value: "compound", label: strings.unitsCompound },
              { value: "ladder", label: strings.unitsLadder },
            ]}
            value={controls.units}
          />
          <Checkbox
            checked={controls.liang}
            label={strings.liang}
            onChange={(value) => set("liang", value)}
          />
          <Checkbox
            checked={controls.circleZero}
            label={strings.circleZero}
            onChange={(value) => set("circleZero", value)}
          />
        </div>
      ) : null}
      {outcome === null ? (
        <Notice>
          {forward ? strings.enterPrompt : strings.enterChinesePrompt}
        </Notice>
      ) : null}
      {outcome !== null && "error" in outcome ? (
        <ErrorNotice>{outcome.error}</ErrorNotice>
      ) : null}
      {outcome !== null && "view" in outcome ? (
        <NumberResult
          lang={outcome.view.lang}
          lines={outcome.view.lines}
          notes={outcome.view.notes}
          strings={{
            heading: forward ? strings.resultHeading : strings.readHeading,
            copy: strings.copy,
            copied: strings.copied,
            rulesHeading: strings.rulesHeading,
          }}
          words={outcome.view.words}
        />
      ) : null}
    </div>
  );
}
